import type { OnboardingStep } from "../../shared/types";
import OnboardingPanel from "frontend/canvas-onboarding/shared/onboarding-panel/onboarding-panel";
import FadeAnimationWrapper from "../../../ui-components/fade-animation/fade-animation-wrapper";
import DeviceSelectionStep from "./device-selection/device-selection-step";
import MouseDragStep from "./device-selection/mouse-drag-step";
import MouseZoomStep from "./device-selection/mouse-zoom-step";
import TrackpadSlideStep from "./device-selection/trackpad-slide-step";
import TrackpadZoomStep from "./device-selection/trackpad-zoom-step";
import BasicToolsStep from "./basic-tools/basic-tools-step";
import TemplateSelection from "./template-selection/template-selection";
import { CIMOnboardingSteps } from "./steps-logic";
import { getMenuItemIdSelector } from "frontend/canvas-designer-new/sidebar/side-menu-items/side-menu-items";
import { CursorType } from "frontend/canvas-designer-new/cursor-type";
import FloatingOnboardingPanel from "frontend/canvas-onboarding/shared/onboarding-panel/floating-onboarding-panel";

const steps: OnboardingStep[] = [
  {
    id: CIMOnboardingSteps.DEVICE_SELECTION,
    render: ({ goToStep, onFinishPhase }) => (
      <FadeAnimationWrapper>
        {(triggerClose) => (
          <DeviceSelectionStep
            onSelect={(device) =>
              triggerClose(() =>
                goToStep(device === "mouse" ? CIMOnboardingSteps.MOUSE_ZOOM : CIMOnboardingSteps.TRACKPAD_ZOOM)
              )
            }
            onDismiss={() => triggerClose(onFinishPhase)}
          />
        )}
      </FadeAnimationWrapper>
    ),
  },
  {
    id: CIMOnboardingSteps.MOUSE_ZOOM,
    render: ({ onFinishPhase, goToStep }) => (
      <FadeAnimationWrapper>
        {(triggerClose) => (
          <OnboardingPanel
            name="onboarding_cim_mouse_zoom"
            description="Scroll the mouse wheel to zoom"
            ctaText="Got it"
            onCtaClick={() => triggerClose(() => goToStep(CIMOnboardingSteps.MOUSE_DRAG))}
            onClose={() => triggerClose(onFinishPhase)}
          >
            <MouseZoomStep
              name="onboarding_cim_mouse_zoom"
              completeStep={() => triggerClose(() => goToStep(CIMOnboardingSteps.MOUSE_DRAG))}
            />
          </OnboardingPanel>
        )}
      </FadeAnimationWrapper>
    ),
  },
  {
    id: CIMOnboardingSteps.MOUSE_DRAG,
    render: ({ onFinishPhase }) => (
      <FadeAnimationWrapper>
        {(triggerClose) => (
          <OnboardingPanel
            name="onboarding_cim_mouse_drag"
            description={"Click the right button and drag\nto move around"}
            ctaText="Got it"
            onCtaClick={() => triggerClose(onFinishPhase)}
            onClose={() => triggerClose(onFinishPhase)}
          >
            <MouseDragStep name="onboarding_cim_mouse_drag" completeStep={() => triggerClose(onFinishPhase)} />
          </OnboardingPanel>
        )}
      </FadeAnimationWrapper>
    ),
  },
  {
    id: CIMOnboardingSteps.TRACKPAD_ZOOM,
    render: ({ goToStep, onFinishPhase }) => (
      <FadeAnimationWrapper>
        {(triggerClose) => (
          <OnboardingPanel
            name="onboarding_cim_trackpad_zoom"
            description="Spread to zoom in"
            ctaText="Got it"
            onCtaClick={() => triggerClose(() => goToStep(CIMOnboardingSteps.TRACKPAD_SLIDE))}
            onClose={() => triggerClose(onFinishPhase)}
          >
            <TrackpadZoomStep
              name="onboarding_cim_trackpad_zoom"
              completeStep={() => triggerClose(() => goToStep(CIMOnboardingSteps.TRACKPAD_SLIDE))}
            />
          </OnboardingPanel>
        )}
      </FadeAnimationWrapper>
    ),
  },
  {
    id: CIMOnboardingSteps.TRACKPAD_SLIDE,
    render: ({ onFinishPhase }) => (
      <FadeAnimationWrapper>
        {(triggerClose) => (
          <OnboardingPanel
            name="onboarding_cim_trackpad_slide"
            description="Slide 2 fingers to move"
            ctaText="Got it"
            onCtaClick={() => triggerClose(onFinishPhase)}
            onClose={() => triggerClose(onFinishPhase)}
          >
            <TrackpadSlideStep name="onboarding_cim_trackpad_slide" completeStep={() => triggerClose(onFinishPhase)} />
          </OnboardingPanel>
        )}
      </FadeAnimationWrapper>
    ),
  },
  {
    id: CIMOnboardingSteps.BASIC_TOOLS,
    render: ({ onFinishPhase }) => (
      <FadeAnimationWrapper>
        {(triggerClose, fadeClassName) => (
          <FloatingOnboardingPanel
            name="onboarding_cim_basic_tools"
            title="Map out your ideas"
            description={
              "Add shapes and sticky notes, fill them\nwith ideas, connect them, and watch\nyour ideas take shape"
            }
            ctaText="Got it"
            onCtaClick={() => triggerClose(onFinishPhase)}
            onClose={() => triggerClose(onFinishPhase)}
            onDismiss={() => triggerClose(onFinishPhase)}
            attachTo={`[id="${getMenuItemIdSelector(CursorType.stickyNote)}"]`}
            className={fadeClassName}
          >
            <BasicToolsStep />
          </FloatingOnboardingPanel>
        )}
      </FadeAnimationWrapper>
    ),
  },
  {
    id: CIMOnboardingSteps.TEMPLATES,
    render: ({ onFinishPhase }) => (
      <FadeAnimationWrapper>
        {(triggerClose) => <TemplateSelection onClose={() => triggerClose(onFinishPhase)} />}
      </FadeAnimationWrapper>
    ),
  },
];

export const CIM_ONBOARDING_STEPS = new Map(steps.map((step) => [step.id, step]));
